import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ComplianceAdminService } from '../../../_core/_services/_compliance/compliance-admin.service';
import { NotificationService } from '../../../_core/_services/notification.service';
import { finalize, forkJoin } from 'rxjs';
import { ApiMeta } from '../../../_core/_model/api-response';

@Component({
  selector: 'app-admin-policies-list',
  standalone: false,
  templateUrl: './admin-policies-list.html',
  styleUrl: './admin-policies-list.css',
})
export class AdminPoliciesList implements OnInit {
  private readonly filterStorageKey = 'admin-compliance-policies-filter';

  meta: ApiMeta | null = null;

  url: any;

  loading = false;
  lookupsLoading = false;
  error: string | null = null;
  items: any[] = [];
  jurisdictions: any[] = [];

  page: number = 1;

  defaultFilter = {
    jurisdictionId: '',
    customerType: '',
    productType: '',
    tierCode: '',
    status: '',
    page: this.page,
    pageSize: ''
  };

  filter = { ...this.defaultFilter };

  policyStatuses = [
    { label: 'Draft', value: 1 },
    { label: 'Active', value: 2 },
    { label: 'Retired', value: 3 }
  ];

  customerTypes = [
    { label: 'Individual', value: 1 },
    { label: 'Business', value: 2 }
  ];

  productTypes = [
    { label: 'Wallet', value: 1 },
    { label: 'Trading', value: 2 },
    { label: 'Remittance', value: 3 }
  ];

  constructor(
    private adminService: ComplianceAdminService,
    private router: Router,
    private notifyService: NotificationService,
  ) {
    this.url = this.router.url.slice(1);
  }

  ngOnInit(): void {
    this.restoreFilters();
    this.loadLookups();
  }

  onPageLoad(page:number) {
    this.page = page;
    this.filter.page = page;
    this.restoreFilters();
    this.loadLookups();
  }

  loadLookups(): void {
    this.lookupsLoading = true;
    this.error = null;

    forkJoin({
      jurisdictions: this.adminService.getJurisdictions({ page: 1, pageSize: 500 })
    })
      .pipe(finalize(() => (this.lookupsLoading = false)))
      .subscribe({
        next: (res: any) => {
          this.meta = res?.meta;
          this.jurisdictions = res?.jurisdictions?.data || res?.jurisdictions || [];
          this.load();
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to load lookup data.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  load(): void {
    this.loading = true;
    this.error = null;

    const payload: any = {
      page: this.filter.page
    };

    if (this.filter.jurisdictionId) payload.jurisdictionId = this.filter.jurisdictionId;
    if (this.filter.customerType) payload.customerType = this.filter.customerType;
    if (this.filter.productType) payload.productType = this.filter.productType;
    if (this.filter.tierCode) payload.tierCode = this.filter.tierCode;
    if (this.filter.status) payload.status = this.filter.status;
    if (this.filter.pageSize) payload.pageSize = this.filter.pageSize;

    this.persistFilters();

    this.adminService.getPolicies(payload).subscribe({
      next: (res: any) => {
        this.items = res?.data || res || [];
        this.loading = false;
      },
      error: (err) => {
        this.error = err?.error?.message || 'Failed to load policies.';
        this.notifyService.showError(this.error!, 'Error');
        this.loading = false;
      }
    });
  }

  createNew(): void {
    this.persistFilters();
    this.router.navigate(['/admin/compliance/policies/new']);
  }

  edit(item: any): void {
    this.persistFilters();
    this.router.navigate(['/admin/compliance/policies', item.id, 'edit']);
  }

  resetFilters(): void {
    this.filter = { ...this.defaultFilter };
    this.clearPersistedFilters();
    this.load();
  }

  persistFilters(): void {
    sessionStorage.setItem(this.filterStorageKey, JSON.stringify(this.filter));
  }

  restoreFilters(): void {
    const raw = sessionStorage.getItem(this.filterStorageKey);
    if (!raw) return;

    try {
      const saved = JSON.parse(raw);
      this.filter = {
        ...this.defaultFilter,
        ...saved,
        page: Number(saved?.page || this.defaultFilter.page),
        pageSize: Number(saved?.pageSize || this.defaultFilter.pageSize)
      };
    } catch {
      this.filter = { ...this.defaultFilter };
    }
  }

  clearPersistedFilters(): void {
    sessionStorage.removeItem(this.filterStorageKey);
  }

  getStatusLabel(value: number): string {
    return this.policyStatuses.find(x => x.value === value)?.label || String(value);
  }

  getStatusBadgeClass(value: number): string {
    if (value === 2) return 'text-bg-success';
    if (value === 3) return 'text-bg-secondary';
    return 'text-bg-warning';
  }

  getCustomerTypeLabel(value: number): string {
    return this.customerTypes.find(x => x.value === value)?.label || String(value);
  }

  getProductTypeLabel(value: number): string {
    return this.productTypes.find(x => x.value === value)?.label || String(value);
  }

  getJurisdictionLabel(item: any): string {
    const match = this.jurisdictions.find(x => x.id === item.jurisdictionId);
    if (match) return `${match.code} - ${match.name}`;
    return item.jurisdictionId || '-';
  }
}